/**
 * Reflection mode — the front-camera turn (TECH_PLAN §1). The work stops showing
 * you the world and turns to look at you: your own face, with the system's read
 * of your state made explicit and legible. A discrete, announced, skippable
 * episode, not ambient surveillance.
 */

import { useEffect, useRef, useState } from 'react';
import { useStore } from '../state/store.js';
import { FaceSensor, type AffectFeatures } from '../sensing/faceSensing.js';
import type { CameraController } from '../passthrough/useCamera.js';

interface Props {
  camera: CameraController;
  onDone: () => void;
}

export function Reflection({ camera, onDone }: Props) {
  const sensingConsent = useStore((s) => s.sensingConsent);
  const setAffect = useStore((s) => s.setAffect);
  const [affect, setLocal] = useState<AffectFeatures | null>(null);
  const [status, setStatus] = useState<string>(sensingConsent ? 'loading sensing…' : 'sensing off');
  const sensorRef = useRef<FaceSensor | null>(null);

  useEffect(() => {
    let raf = 0;
    let cancelled = false;

    void camera.start('user');

    async function run() {
      if (!sensingConsent) {
        setStatus('sensing off — the work is only looking, not reading');
        return;
      }
      const sensor = new FaceSensor();
      sensorRef.current = sensor;
      try {
        await sensor.load();
        if (cancelled) return;
        setStatus('reading, on your device only');
        const loop = () => {
          const v = camera.videoRef.current;
          if (v) {
            const a = sensor.detect(v);
            setLocal(a);
            setAffect(a);
          }
          raf = requestAnimationFrame(loop);
        };
        raf = requestAnimationFrame(loop);
      } catch {
        setStatus('sensing unavailable — continuing without it');
      }
    }
    void run();

    return () => {
      cancelled = true;
      cancelAnimationFrame(raf);
      // Release the FRONT camera on the way out. The Field restarts the back one
      // when it mounts, but nothing else guarantees the face stream ends, and
      // the Watcher must never be left reading from it.
      camera.stop();
      sensorRef.current?.close();
      sensorRef.current = null;
      setAffect(null);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sensingConsent]);

  return (
    <div className="reflection">
      <video className="passthrough mirror" ref={camera.videoRef} autoPlay muted playsInline />
      <div className="reflection-scrim" />
      <div className="reflection-hud">
        <h2>It turned to look at you.</h2>
        <p className="status">{status}</p>

        {affect?.present ? (
          <div className="affect">
            <Meter label="valence" value={(affect.valence + 1) / 2} />
            <Meter label="arousal" value={affect.arousal} />
            <Meter label="attention" value={affect.attention} />
          </div>
        ) : sensingConsent ? (
          <p className="status dim">no face in frame</p>
        ) : null}

        <button className="primary" onClick={onDone}>
          Return to the field
        </button>
      </div>
    </div>
  );
}

function Meter({ label, value }: { label: string; value: number }) {
  return (
    <div className="meter">
      <span className="meter-label">{label}</span>
      <div className="meter-track">
        <div className="meter-fill" style={{ width: `${Math.round(Math.max(0, Math.min(1, value)) * 100)}%` }} />
      </div>
    </div>
  );
}
